import { useState } from "react";
import { Link } from "react-router-dom";
import { descargar, subirArchivo } from "../lib/api";
import {
  Aviso,
  Cabecera,
  Cargando,
  Panel,
  TarjetaDato,
  Vacio,
} from "../componentes/piezas";
import { creditos, formatearCantidad } from "../lib/formato";
import { useIdioma } from "../lib/idioma";

interface Informe {
  simulado: boolean;
  total: number;
  validas: number;
  conStock: number;
  errores: { fila: number; mensaje: string }[];
  insertadas: number;
  muestra: {
    nombre: string;
    categoria: string;
    unidad: string;
    precioCentimos: number;
    stockMg: number;
  }[];
  columnasReconocidas: string[];
}

/**
 * Importación del catálogo y el stock inicial desde un CSV.
 *
 * Es el segundo fichero de una migración, después del de socios, y el que
 * decide si el club se cambia: cuarenta productos con sus lotes a mano es la
 * excusa perfecta para quedarse donde está. Misma mecánica que con los socios:
 * primero se analiza sin guardar nada; solo si no hay errores se importa.
 */
export default function CatalogoImportar() {
  const { t, cuenta } = useIdioma();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [informe, setInforme] = useState<Informe | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");

  async function analizar() {
    if (!archivo) return;
    setError("");
    setTrabajando(true);
    try {
      setInforme(
        await subirArchivo<Informe>("/catalogo/importar?simular=1", archivo),
      );
    } catch (fallo) {
      setInforme(null);
      setError(
        fallo instanceof Error ? fallo.message : t("ci2.falloLeer"),
      );
    } finally {
      setTrabajando(false);
    }
  }

  async function importar() {
    if (!archivo) return;
    setError("");
    setTrabajando(true);
    try {
      setInforme(await subirArchivo<Informe>("/catalogo/importar", archivo));
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("ci2.falloImportar"));
    } finally {
      setTrabajando(false);
    }
  }

  const importado = informe && !informe.simulado && informe.insertadas > 0;

  return (
    <>
      <Cabecera
        titulo={t("ci2.titulo")}
        descripcion={t("ci2.descripcion")}
      >
        <Link className="boton secundario" to="/catalogo">
          {t("ci2.volver")}
        </Link>
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      {importado ? (
        <Panel>
          <Aviso tipo="ok">
            {informe.insertadas === 1
              ? informe.conStock > 0
                ? t("ci2.importadoUnoConStock")
                : t("ci2.importadoUno")
              : informe.conStock > 0
                ? t("ci2.importadosConStock", {
                    n: informe.insertadas,
                    conStock: informe.conStock,
                  })
                : t("ci2.importados", { n: informe.insertadas })}
          </Aviso>
          <div className="fila">
            <Link className="boton" to="/catalogo">
              {t("ci2.verCatalogo")}
            </Link>
            <Link className="boton secundario" to="/inventario">
              {t("ci2.verInventario")}
            </Link>
            <button
              className="boton secundario"
              onClick={() => {
                setArchivo(null);
                setInforme(null);
              }}
            >
              {t("ci2.otroFichero")}
            </button>
          </div>
        </Panel>
      ) : (
        <div className="rejilla dos" style={{ alignItems: "start" }}>
          <div className="columna" style={{ gap: 16 }}>
            <Panel titulo={t("ci2.paso1")}>
              {/* Los nombres de las columnas son las cabeceras que el fichero
                  tiene que llevar, no texto de la pantalla; el importador las
                  reconoce en castellano y en catalan (ver ALIAS en csvLeer). */}
              <p className="suave" style={{ marginTop: 0 }}>
                {t("ci2.explicacion")}
              </p>
              <p className="suave pequeno">{t("ci2.formatos")}</p>
              <button
                className="boton secundario"
                onClick={() =>
                  descargar(
                    "/catalogo/importar/plantilla.csv",
                    "plantilla-productos.csv",
                  ).catch((fallo) =>
                    setError(
                      fallo instanceof Error ? fallo.message : t("ci2.falloPlantilla"),
                    ),
                  )
                }
              >
                {t("ci2.descargarPlantilla")}
              </button>
            </Panel>

            <Panel titulo={t("ci2.paso2")}>
              <input
                type="file"
                accept=".csv,.txt,text/csv"
                onChange={(e) => {
                  setArchivo(e.target.files?.[0] ?? null);
                  setInforme(null);
                  setError("");
                }}
              />
              <p className="suave pequeno">{t("ci2.analizarNoGuarda")}</p>
              <button
                className="boton"
                disabled={!archivo || trabajando}
                onClick={analizar}
              >
                {trabajando && !informe ? t("ci2.analizando") : t("ci2.analizar")}
              </button>
            </Panel>
          </div>

          <Panel titulo={t("ci2.paso3")} sinCuerpo>
            {trabajando ? (
              <Cargando texto={t("ci2.procesando")} />
            ) : !informe ? (
              <Vacio>{t("ci2.eligeFichero")}</Vacio>
            ) : (
              <>
                <div className="panel-cuerpo">
                  <div className="rejilla tres">
                    <TarjetaDato etiqueta={t("ci2.filas")} valor={informe.total} />
                    <TarjetaDato
                      etiqueta={t("ci2.validas")}
                      valor={informe.validas}
                      color="var(--verde)"
                    />
                    <TarjetaDato
                      etiqueta={t("ci2.conErrores")}
                      valor={informe.errores.length}
                      color={
                        informe.errores.length > 0 ? "var(--rojo)" : undefined
                      }
                    />
                  </div>
                  <p className="suave pequeno" style={{ marginBottom: 0 }}>
                    {t("ci2.columnas", { columnas: informe.columnasReconocidas.join(", ") })}
                  </p>
                </div>

                {informe.errores.length > 0 ? (
                  <>
                    <div className="panel-cuerpo" style={{ paddingTop: 0 }}>
                      <Aviso tipo="error">{t("ci2.corrige")}</Aviso>
                    </div>
                    <div
                      className="tabla-envoltorio"
                      style={{ maxHeight: 360, overflowY: "auto" }}
                    >
                      <table className="tabla">
                        <thead>
                          <tr>
                            <th>{t("ci2.fila")}</th>
                            <th>{t("ci2.problema")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {informe.errores.map((e) => (
                            <tr key={e.fila}>
                              <td className="tabular">{e.fila}</td>
                              <td className="pequeno">{e.mensaje}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="tabla-envoltorio">
                      <table className="tabla">
                        <thead>
                          <tr>
                            <th>{t("ci2.producto")}</th>
                            <th>{t("ci2.categoria")}</th>
                            <th className="num">{t("ci2.precio")}</th>
                            <th className="num">{t("ci2.stock")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {informe.muestra.map((p) => (
                            <tr key={`${p.categoria}|${p.nombre}`}>
                              <td>{p.nombre}</td>
                              <td className="pequeno">{p.categoria}</td>
                              <td className="num tabular">
                                {creditos(p.precioCentimos)}/
                                {p.unidad === "UNIDAD" ? "ud" : "g"}
                              </td>
                              <td className="num tabular">
                                {p.stockMg > 0 ? (
                                  formatearCantidad(p.stockMg, p.unidad)
                                ) : (
                                  <span className="suave">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="panel-cuerpo">
                      {informe.validas > informe.muestra.length && (
                        <p className="suave pequeno" style={{ marginTop: 0 }}>
                          {t("ci2.seMuestran", {
                            cuantos: informe.muestra.length,
                            total: informe.validas,
                          })}
                        </p>
                      )}
                      <button
                        className="boton grande"
                        disabled={trabajando}
                        onClick={importar}
                      >
                        {cuenta(informe.validas, "ci2.importarUno", "ci2.importarVarios")}
                        {informe.conStock > 0 && t("ci2.conStock", { n: informe.conStock })}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </Panel>
        </div>
      )}
    </>
  );
}
